import React, { Fragment, useState, useEffect } from "react";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { CommonUtils, StorageUtils, DatetimeUtils } from "utils";
import "chart.js/auto";
import { Doughnut, Bar } from "react-chartjs-2";
import { useStatisticMessageGet } from "hooks";
import { useTranslation } from "react-i18next";
import { KeyConstant, LangConstant } from "const";
import SelectDateTime from "./SelectDateTime";

const CallStatistic = ({ unitList }) => {
  const { t: getLabel } = useTranslation();
  const { getStatisticMessage } = useStatisticMessageGet();

  const [unitId, setUnitId] = useState("0");
  const [labelCalendar, setLabelCalendar] = useState(getLabel(LangConstant.TXT_DATE_TODAY));
  const [totalCall, setTotalCall] = useState(0);
  const [dataBarCall, setDataBarCall] = useState("");
  const [dataDoughnutCall, setDataDoughnutCall] = useState("");
  const [optionsDoughtnutCall, setOptionsDoughtnutCall] = useState("");
  const [sinceTime, setSinceTime] = useState(CommonUtils.getStartToday());
  const [lastTime, setLastTime] = useState(null);

  const [openDateTime, setOpenDateTime] = useState(false);

  const onChangeUnit = e => {
    setUnitId(String(e.target.value));
  };

  useEffect(() => {
    const fetchData = async () => {
      const payload = {};
      if (String(unitId) === "0") {
        payload.include_unit_id = 0;
      } else {
        payload.include_unit_id = 1;
      }
      if (String(unitId) !== "0" && String(unitId) !== "-1") {
        payload.unit_id = unitId;
      }
      if (sinceTime) {
        payload.since_time = sinceTime;
      }
      if (lastTime) {
        payload.last_time = lastTime;
      }

      const response = await getStatisticMessage(payload);
      if (response) {
        const responseData = response.data.sort((a, b) => {
          const dateA = new Date(a.date.split("/").reverse().join("-"));
          const dateB = new Date(b.date.split("/").reverse().join("-"));
          return dateA - dateB;
        });
        const labelBar = responseData.map(item => item.date);
        const dataBar = responseData.map(item => item.call_accept + item.call_missed + item.call_reject);
        const dataDoughnut = response.data.reduce(
          (acc, item) => {
            acc[0] += item.call_accept;
            acc[1] += item.call_missed;
            acc[2] += item.call_reject;
            return acc;
          },
          [0, 0, 0],
        );

        setTotalCall(dataBar.reduce((sum, element) => sum + element, 0));

        setDataBarCall({
          labels: labelBar,
          datasets: [
            {
              label: getLabel(LangConstant.TXT_STATISTIC_NUM_CALLS),
              data: dataBar,
              backgroundColor: ["#357AF6"],
            },
          ],
        });

        setDataDoughnutCall({
          labels: [
            getLabel(LangConstant.TXT_STATISTIC_CALL_SUCCESS),
            getLabel(LangConstant.TXT_STATISTIC_CALL_MISSED),
            getLabel(LangConstant.TXT_STATISTIC_CALL_CANCELED),
          ],
          datasets: [
            {
              label: getLabel(LangConstant.TXT_STATISTIC_NUM_CALLS),
              data: dataDoughnut,
              backgroundColor: dataDoughnut.map(() => CommonUtils.getRandomColor()),
              hoverOffset: 4,
            },
          ],
        });

        setOptionsDoughtnutCall({
          cutout: "70%",
          radius: "80%",
          responsive: true,
          plugins: {
            legend: {
              position: "bottom",
              maxWidth: 220,
              labels: {
                usePointStyle: false,
                boxWidth: 15,
                boxHeight: 15,
              },
            },
          },
        });
      }
    };

    fetchData();
  }, [unitId, sinceTime, lastTime]);

  return (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
        <h3 className="m-0">{getLabel(LangConstant.TXT_STATISTIC_CALL_OVERVIEW)}</h3>
        <div className="d-flex gap-3 position-relative">
          <select
            className="form-select"
            style={{ width: "200px" }}
            onChange={onChangeUnit}
            value={unitId}
          >
            {unitList.map(data => (
              <option key={data.unitId} value={data.unitId}>
                {data.unitName}
              </option>
            ))}
          </select>

          <div
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            style={{ minWidth: "200px", maxWidth: "250px" }}
            onClick={() => setOpenDateTime(!openDateTime)}
          >
            <IconCalendar size={18} />
            <span className="flex-grow-1">{labelCalendar}</span>
            <IconChevronDown size={16} />
          </div>

          <div style={{ position: "absolute", zIndex: 4, top: "42px", right: "0px" }}>
            <SelectDateTime
              open={openDateTime}
              setLabel={setLabelCalendar}
              onClose={() => setOpenDateTime(false)}
              setSinceTime={setSinceTime}
              setLastTime={setLastTime}
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-around align-items-center" style={{ height: "550px" }}>
        <div className="card position-relative" style={{ width: "60%", height: "90%" }}>
          <div className="card-body">
            <h4 className="card-title d-flex align-items-center">
              {getLabel(LangConstant.TXT_STATISTIC_TOTAL_CALLS_MADE)}
              <span className="ms-2 fs-3 fw-bold text-primary">{totalCall} {getLabel(LangConstant.TXT_STATISTIC_CALLS_UNIT)}</span>
            </h4>
            <div className="d-flex flex-column align-items-center w-100">
              <div style={{ width: "90%" }}>{Boolean(dataBarCall) && <Bar data={dataBarCall} />}</div>
            </div>
          </div>
        </div>

        <div className="card position-relative" style={{ width: "35%", height: "90%" }}>
          <div className="card-body d-flex flex-column">
            <h4 className="card-title">{getLabel(LangConstant.TXT_STATISTIC_CALL_STATUS_RATIO)}</h4>
            <div className="d-flex flex-column align-items-center w-100 flex-grow-1 justify-content-center position-relative">
              <div style={{ width: "100%", maxWidth: "300px" }}>
                {Boolean(dataDoughnutCall) && <Doughnut data={dataDoughnutCall} options={optionsDoughtnutCall} />}
              </div>
              <div className="d-flex flex-column align-items-center position-absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}>
                <span className="text-secondary" style={{ fontSize: "12px" }}>{getLabel(LangConstant.TXT_STATISTIC_TOTAL_CALLS)}</span>
                <strong style={{ fontSize: "18px" }}>{totalCall}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-secondary small">
        {getLabel(LangConstant.TXT_STATISTIC_DATA_UPDATED_AT, { time: DatetimeUtils.convertMillisecondsToDateTime(Date.now()) })}
      </p>
    </Fragment>
  );
};

export default CallStatistic;
